import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight, Loader2, ArrowUp } from "lucide-react";
import { GithubIcon, LinkedinIcon, TelegramIcon, XIcon } from "./icons";
import { DiscordIcon } from "./icons/discord";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { delay } from "motion/react";

gsap.registerPlugin(ScrollTrigger);

const PLACEHOLDERS = [
  "i have an idea...",
  "are you open for...",
  "what is your view on...",
  "let's work together on...",
];

async function getContactToken(): Promise<string> {
  const response = await fetch("/api/contact-token", {
    method: "POST",
  });

  if (!response.ok) {
    throw new Error("Failed to issue contact token");
  }

  const payload = (await response.json()) as { token?: unknown };
  if (typeof payload.token !== "string" || payload.token.length === 0) {
    throw new Error("Missing contact token");
  }

  return payload.token;
}

export function Contact() {
  const containerRef = useRef<HTMLElement>(null);
  const bordersRef = useRef<(HTMLDivElement | null)[]>([]);
  const slotMachineRef = useRef<HTMLDivElement>(null);
  const bottomElementsRef = useRef<(HTMLElement | null)[]>([]);
  const reachOutRef = useRef<HTMLHeadingElement>(null);
  const reachOutWrapperRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({ message: "", contactInfo: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [time, setTime] = useState("");

  const [messageFocused, setMessageFocused] = useState(false);
  const [replyFocused, setReplyFocused] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      setTime(
        new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }),
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Unified Entrance Animations
      gsap.set(bordersRef.current, { scaleX: 0, transformOrigin: "left" });
      gsap.set(bottomElementsRef.current, { y: 30, opacity: 0 });
      gsap.set(reachOutRef.current, {
        opacity: 0,
        rotationX: 45,
        scaleY: 1.2,
        transformPerspective: 1000,
        transformOrigin: "bottom center",
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
        },
      });

      // Synchronised macro-animations
      tl.to(bordersRef.current, {
        scaleX: 1,
        duration: 1.2,
        stagger: 0.2,
        ease: "power3.inOut",
      })
        .to(
          bottomElementsRef.current,
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: "back.out(1.5)",
          },
          "-=0.8",
        )
        .to(
          reachOutRef.current,
          {
            opacity: 1,
            rotationX: 30,
            scaleY: 1.0,
            duration: 1.5,
            ease: "back.out(1.2)",
          },
          "+=0.3",
        );

      // 2. Slot Machine Carousel Placeholders
      const slotItems = gsap.utils.toArray(
        ".slot-item",
        slotMachineRef.current,
      );
      if (slotItems.length > 0) {
        gsap.set(slotItems, { yPercent: 100, autoAlpha: 0 });
        gsap.set(slotItems[0]!, { yPercent: 0, autoAlpha: 1 });

        let currentIndex = 0;
        let rollTween: gsap.core.Timeline | null = null;

        const rollPlaceholder = () => {
          const outgoing = slotItems[currentIndex];
          currentIndex = (currentIndex + 1) % slotItems.length;
          const incoming = slotItems[currentIndex];

          rollTween = gsap.timeline({
            onComplete: () => {
              gsap.delayedCall(2.2, rollPlaceholder);
            },
          });
          rollTween.to(
            outgoing as Element,
            {
              yPercent: -100,
              autoAlpha: 0,
              duration: 0.6,
              ease: "power3.inOut",
            },
            0,
          );
          rollTween.fromTo(
            incoming as Element,
            { yPercent: 100, autoAlpha: 0 },
            { yPercent: 0, autoAlpha: 1, duration: 0.6, ease: "power3.inOut" },
            0,
          );
        };

        gsap.delayedCall(2.2, rollPlaceholder);
      }
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contactToken = await getContactToken();
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Contact-Token": contactToken,
        },
        body: JSON.stringify(formData),
      });

      if (res.status === 403) {
        toast.error("This request was blocked. Refresh and try again.");
        return;
      }

      if (res.status === 429) {
        toast.warning("Too many requests. Please try again later.");
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to send message");
      }

      const data: Record<string, unknown> = await res.json();
      if (data.success) {
        toast.success("Message sent successfully!");
        setFormData({ message: "", contactInfo: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const isMessageActive = messageFocused || formData.message.length > 0;

  return (
    <section
      ref={containerRef}
      data-section="contact"
      className="relative z-40 bg-background text-foreground flex flex-col justify-between overflow-hidden pt-32 pb-0 min-h-[min(100vh,1280px)]"
    >
      <div className="container mx-auto px-6 w-full overflow-hidden flex flex-col flex-1 relative z-10 mb-8 md:mb-12">
        <div className="mb-16 md:mb-20">
          <div className="text-base text-muted-foreground font-mono uppercase tracking-[0.2em] border-l-2 border-blue-500 pl-4 text-left">
            03 / Contact
          </div>
        </div>

        {/* Input Box / Form Interface */}
        <form
          onSubmit={handleSubmit}
          className="relative flex flex-col flex-1 justify-center z-20 w-full"
        >
          <div className="w-full flex flex-col relative">
            <div
              className="mt-8 mb-2 relative w-full h-16 md:h-20"
              ref={(el) => {
                bottomElementsRef.current[1] = el;
              }}
            >
              {/* GSAP Managed Slot-Machine */}
              <div
                ref={slotMachineRef}
                className="absolute inset-x-0 bottom-0 h-full pointer-events-none overflow-hidden transition-opacity duration-300 z-0 bg-transparent"
                style={{ opacity: formData.message.length > 0 ? 0 : 1 }}
              >
                {PLACEHOLDERS.map((text, i) => (
                  <div
                    key={i}
                    className="slot-item absolute inset-0 flex items-center"
                  >
                    <span className="text-3xl md:text-5xl font-medium text-transparent bg-clip-text bg-linear-to-br from-foreground/40 to-muted-foreground/50 pb-2">
                      {text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Core Textarea -> Swapped to single line input to match cursor perfectly */}
              <input
                type="text"
                id="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                onFocus={() => setMessageFocused(true)}
                onBlur={() => setMessageFocused(false)}
                className="absolute inset-x-0 bottom-0 w-full h-full bg-transparent text-3xl md:text-5xl font-medium text-foreground border-none focus:ring-0 px-0 pb-2 transition-all duration-300 rounded-none outline-none z-10"
                autoComplete="off"
                required
              />
            </div>

            {/* Base Border (Bottom of textarea) */}
            <div
              ref={(el) => {
                bordersRef.current[0] = el;
              }}
              className={cn(
                "w-full h-0.5 origin-left transition-colors duration-500",
                isMessageActive ? "bg-blue-500" : "bg-muted-foreground/40",
              )}
            ></div>
          </div>

          <div
            ref={(el) => {
              bottomElementsRef.current[2] = el;
            }}
            className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4 pt-4 md:pt-8 mb-24 md:mb-32 w-full px-0"
          >
            <div className="w-full md:w-2/3 flex flex-col justify-end h-full">
              <label
                htmlFor="contactInfo"
                className="block text-xs md:text-lg text-muted-foreground mb-1 font-mono tracking-widest leading-none"
              >
                how do i reply
              </label>
              <input
                type="text"
                id="contactInfo"
                value={formData.contactInfo}
                onChange={(e) =>
                  setFormData({ ...formData, contactInfo: e.target.value })
                }
                onFocus={() => setReplyFocused(true)}
                onBlur={() => setReplyFocused(false)}
                className="w-full bg-transparent text-base md:text-xl text-foreground placeholder:text-muted-foreground/40 border-none focus:ring-0 py-2 transition-all duration-300 rounded-none outline-none h-12 leading-none"
                placeholder="email, telegram, post office..."
                required
              />
            </div>

            {/* Nav style button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative flex items-center justify-center gap-2 bg-blue-500 text-white font-medium hover:bg-blue-600 transition-all hover:-translate-y-px rounded-2xl px-5 py-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mx-auto md:ml-auto"
            >
              {isSubmitting ? (
                <Loader2 className="animate-spin w-5 h-5" />
              ) : (
                <>
                  <span className="text-lg relative z-10">Send Message</span>
                  <div className="w-0 opacity-0 group-hover:w-4 group-hover:opacity-100 overflow-hidden transition-all duration-300 transform group-hover:translate-x-1 relative z-10 flex items-center">
                    <ArrowUpRight className="w-4 h-4 shrink-0" />
                  </div>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Socials Block Between 2 Horizontal Rules */}
        <div className="w-full relative mt-auto z-20">
          <div
            ref={(el) => {
              bordersRef.current[1] = el;
            }}
            className="w-full h-0.5 bg-muted-foreground/40 origin-left absolute top-0"
          ></div>

          <div
            ref={(el) => {
              bottomElementsRef.current[3] = el;
            }}
            className="flex flex-col md:flex-row justify-between items-center py-6 md:py-8 px-2"
          >
            <span className="text-xs md:text-lg font-mono text-muted-foreground lowercase tracking-wide mb-6 md:mb-0 flex items-center">
              other channels{" "}
              <span className="text-blue-500 ml-2 font-bold">&gt;&gt;</span>
            </span>

            <div className="flex flex-wrap items-center justify-center md:justify-end gap-3 md:gap-5">
              {[
                { Icon: XIcon, href: "https://x.com/code_z2" },
                { Icon: GithubIcon, href: "https://github.com/code-z2" },
                { Icon: LinkedinIcon, href: "https://linkedin.com/in/code-z2" },
                {
                  Icon: DiscordIcon,
                  href: "https://discord.com/users/700327336507080734",
                },
                { Icon: TelegramIcon, href: "https://t.me/code_z2" },
              ].map(({ Icon, href }, index) => (
                <a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-foreground hover:text-background transition-all hover:scale-105"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div
            ref={(el) => {
              bordersRef.current[2] = el;
            }}
            className="w-full h-0.5 bg-muted-foreground/40 origin-left absolute bottom-0"
          ></div>
        </div>
      </div>

      {/* Massive Base-Skewed REACH OUT Text */}
      <div
        ref={reachOutWrapperRef}
        className="w-full hidden md:flex justify-center mt-auto relative z-0 mb-4 md:mb-6"
        style={{ perspective: "1000px" }}
      >
        <h1
          ref={reachOutRef}
          className="text-[34vw] md:text-[30vw] lg:text-[27vw] font-black tracking-tighter leading-[0.75] text-foreground uppercase select-none mix-blend-screen relative z-10 scale-y-110 pointer-events-auto whitespace-nowrap"
        >
          REACH OUT
        </h1>
      </div>

      {/* Mobile visible fallback without the tracker */}
      <div
        className="w-full flex justify-center mt-auto relative z-0 mb-4 md:mb-6 md:hidden overflow-hidden pointer-events-none"
        style={{ perspective: "800px" }}
      >
        <h1
          className="text-[22vw] font-black tracking-tighter leading-[0.75] text-foreground uppercase select-none mix-blend-screen scale-y-110 whitespace-nowrap"
          style={{
            transform: "rotateX(25deg)",
            transformOrigin: "bottom center",
          }}
        >
          REACH OUT
        </h1>
      </div>

      {/* Extreme Bottom Line: Copyright, Blog, Time, ScrollUp */}
      <div className="container mx-auto px-6 w-full flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm text-muted-foreground font-mono uppercase tracking-widest md:tracking-[0.15em] relative z-20">
        <div
          ref={(el) => {
            bottomElementsRef.current[4] = el;
          }}
          className="flex flex-col md:flex-row items-center gap-2 md:gap-6"
        >
          <span>© {new Date().getFullYear()} ALL RIGHTS RESERVED</span>
          <a
            href="https://paragraph.com/@anyaogu"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors underline underline-offset-4"
          >
            BLOG
          </a>
        </div>

        <span
          ref={(el) => {
            bottomElementsRef.current[5] = el;
          }}
        >
          {time}
        </span>

        <button
          ref={(el) => {
            bottomElementsRef.current[6] = el;
          }}
          onClick={scrollToTop}
          className="hover:text-foreground transition-colors md:flex items-center gap-2 hidden"
        >
          GO BACK UP <ArrowUp size={16} />
        </button>
      </div>
    </section>
  );
}
